import {APIRegistry} from '@spothero/utils/api';
import ErrorUtils from 'utils/error-utils';
import Config from 'config';

const createAPIRegistry = () => {
    if (!APIRegistry.get('craig')) {
        APIRegistry.create({
            name: 'craig',
            config: {
                baseUrl: Config.craigUrl,
            },
        });
    }
};

const transformToRange = (lowValue, highValue) => {
    const low = parseFloat(lowValue);
    const high = parseFloat(highValue);

    return Boolean(low) && Boolean(high) ? {low, high} : null;
};

const DynamicRatesAPI = {
    /**
     * Gets a list of aggregated rates for given city.
     *
     * @function getRatesByCity
     * @see {@link https://api.spothero.com/v2/docs#operation/fetchCityRates|Documentation}
     * @param {object} citySlug - The monolith-defined city slug to pass to the endpoint.
     * @example
     * getRatesByCity('chicago');
     * @returns {object} Currency and aggregated rates for the city, with data transformation applied.
     */
    getRatesByCity: citySlug => {
        createAPIRegistry();

        return APIRegistry.get('craig')
            .get(`/midfunnel-rates/cities/${citySlug}/`)
            .then(({data}) => {
                const {
                    currency,
                    commuter_avg_price_low: commuterAvgPriceLow,
                    commuter_avg_price_high: commuterAvgPriceHigh,
                    weekend_avg_price_low: weekendAvgPriceLow,
                    weekend_avg_price_high: weekendAvgPriceHigh,
                    event_avg_price_low: eventAvgPriceLow,
                    event_avg_price_high: eventAvgPriceHigh,
                    overnight_avg_price_low: overnightAvgPriceLow,
                    overnight_avg_price_high: overnightAvgPriceHigh,
                    monthly_avg_price_low: monthlyAvgPriceLow,
                    monthly_avg_price_high: monthlyAvgPriceHigh,
                    airport_avg_price_low: airportAvgPriceLow,
                    airport_avg_price_high: airportAvgPriceHigh,
                } = data;

                const transformedData = {
                    currencyCode: currency.toUpperCase(),
                    commuterAvgPrice: transformToRange(
                        commuterAvgPriceLow,
                        commuterAvgPriceHigh
                    ),
                    weekendAvgPrice: transformToRange(
                        weekendAvgPriceLow,
                        weekendAvgPriceHigh
                    ),
                    eventAvgPrice: transformToRange(
                        eventAvgPriceLow,
                        eventAvgPriceHigh
                    ),
                    overnightAvgPrice: transformToRange(
                        overnightAvgPriceLow,
                        overnightAvgPriceHigh
                    ),
                    monthlyAvgPrice: transformToRange(
                        monthlyAvgPriceLow,
                        monthlyAvgPriceHigh
                    ),
                    airportAvgPrice: transformToRange(
                        airportAvgPriceLow,
                        airportAvgPriceHigh
                    ),
                };

                return transformedData;
            })
            .catch(error => {
                ErrorUtils.sendSentryError(error);

                return null;
            });
    },
    /**
     * Gets a list of aggregated rates for given destination.
     *
     * @function getRatesByDestinationID
     * @see {@link https://api.spothero.com/v2/docs#operation/fetchDestinationRates|Documentation}
     * @param {object} destinationID - The monolith-defined destination ID to pass to the endpoint.
     * @example
     * getRatesByDestinationID(1);
     * @returns {object} Currency and aggregated rates for the destination, with data transformation applied.
     */
    getRatesByDestinationID: destinationID => {
        createAPIRegistry();

        return APIRegistry.get('craig')
            .get(`/midfunnel-rates/destinations/${destinationID}/`)
            .then(({data}) => {
                const {
                    currency,
                    commuter_avg_price_low: commuterAvgPriceLow,
                    commuter_avg_price_high: commuterAvgPriceHigh,
                    weekend_avg_price_low: weekendAvgPriceLow,
                    weekend_avg_price_high: weekendAvgPriceHigh,
                    event_avg_price_low: eventAvgPriceLow,
                    event_avg_price_high: eventAvgPriceHigh,
                    overnight_avg_price_low: overnightAvgPriceLow,
                    overnight_avg_price_high: overnightAvgPriceHigh,
                    monthly_avg_price_low: monthlyAvgPriceLow,
                    monthly_avg_price_high: monthlyAvgPriceHigh,
                    airport_avg_price_low: airportAvgPriceLow,
                    airport_avg_price_high: airportAvgPriceHigh,
                } = data;

                const transformedData = {
                    currencyCode: currency.toUpperCase(),
                    commuterAvgPrice: transformToRange(
                        commuterAvgPriceLow,
                        commuterAvgPriceHigh
                    ),
                    weekendAvgPrice: transformToRange(
                        weekendAvgPriceLow,
                        weekendAvgPriceHigh
                    ),
                    eventAvgPrice: transformToRange(
                        eventAvgPriceLow,
                        eventAvgPriceHigh
                    ),
                    overnightAvgPrice: transformToRange(
                        overnightAvgPriceLow,
                        overnightAvgPriceHigh
                    ),
                    monthlyAvgPrice: transformToRange(
                        monthlyAvgPriceLow,
                        monthlyAvgPriceHigh
                    ),
                    airportAvgPrice: transformToRange(
                        airportAvgPriceLow,
                        airportAvgPriceHigh
                    ),
                };

                return transformedData;
            })
            .catch(error => {
                ErrorUtils.sendSentryError(error);

                return null;
            });
    },
};

export default DynamicRatesAPI;
